import React from 'react';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import Client from './components/Client';
import Config from './components/ClientConfig';
import Message from './components/Message';
import MessagesFilters from './components/MessagesFilters';
import Visibility from './Visibility';

export default class MessagesList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      client: new Client('none'),
      messages: null, // null => loading state (shows skeleton)
      addNew: false,
      filterData: undefined,
      limit: 20,
      hasMore: false,
      loadingMore: false,
      error: null,
    };

    this.applyFilters = this.applyFilters.bind(this);
    this.toggleAdd = this.toggleAdd.bind(this);
    this.messageUpdated = this.messageUpdated.bind(this);
    this.getData = this.getData.bind(this);

    this.restricted = new Visibility('messages');
    this.filtersRef = React.createRef();
  }

  componentDidMount() {
    if (this.restricted.scope.visible) this.getData();
  }

  extractMessages(data) {
    if (Array.isArray(data?.messages)) return data.messages;
    if (Array.isArray(data)) return data;
    return null;
  }

  loadMessages(filters, limit, loadingMore = false) {
    const safeLimit = Math.min(Math.max(limit || 20, 20), 200);
    const queryData = {
      orderBy_createdOn: true,
      ...(filters || {}),
      limit: safeLimit,
    };

    this.setState({ error: null, loadingMore });
    try {
      const data = this.state.client.getData(Config.API_URL_MESSAGE, queryData);
      const messages = this.extractMessages(data);
      if (!messages) throw new Error('The server returned an invalid message list.');

      this.setState({
        messages,
        filterData: filters,
        limit: safeLimit,
        hasMore: messages.length === safeLimit && safeLimit < 200,
        loadingMore: false,
        error: null,
      });
    } catch (error) {
      console.error('MessagesList.loadMessages error:', error);
      this.setState((state) => ({
        messages: state.messages === null ? [] : state.messages,
        loadingMore: false,
        error: 'Messages could not be loaded. Please retry.',
      }));
    }
  }

  /**
   * Fetch initial list
   */
  getData(limit = this.state.limit) {
    this.loadMessages(undefined, limit);
  }

  /**
   * Fetch with filters applied
   */
  applyFilters(queryData) {
    const filters = {
      orderBy_createdOn: true,
      ...(queryData || {}),
    };
    delete filters.limit;
    this.loadMessages(filters, 20);
  }

  toggleAdd() {
    if (!this.restricted.scope.edit) return;
    this.setState((s) => ({ addNew: !s.addNew }));
    window.scrollTo(0, 0);
  }

  messageUpdated(savedItem, previousIref) {
    const refreshCurrent = () => {
      this.loadMessages(this.state.filterData, this.state.limit);
    };
    if (savedItem) {
      this.setState((state) => {
        const current = Array.isArray(state.messages) ? state.messages : [];
        const existingIndex = current.findIndex((item) => (
          String(item.iref) === String(previousIref)
          || String(item.iref) === String(savedItem.iref)
        ));
        const messages = [...current];
        if (existingIndex >= 0) messages[existingIndex] = savedItem;
        else messages.unshift(savedItem);
        return { messages, addNew: false, error: null };
      }, refreshCurrent);
    } else {
      this.setState({ addNew: false }, refreshCurrent);
    }

    if (this.filtersRef?.current) {
      this.filtersRef.current.setState({ addNew: false });
    }
  }

  loadMore = () => {
    if (this.state.loadingMore || !this.state.hasMore) return;
    this.loadMessages(
      this.state.filterData,
      Math.min(this.state.limit + 20, 200),
      true,
    );
  };

  retry = () => {
    this.loadMessages(this.state.filterData, this.state.limit);
  };

  render() {
    if (!this.restricted.scope.visible) return <div />;

    const { messages } = this.state;
    const isArray = Array.isArray(messages);
    const counter = isArray ? messages.length : 0;

    const list = isArray
      ? messages.map((item) => (
          <Message
            key={item.iref}
            item={item}
            onUpdated={this.messageUpdated}
            allowEdit={this.restricted.scope.edit}
            API_URL_MESSAGES={this.props.API_URL_MESSAGES}
          />
        ))
      : null;

    return (
      <div style={{ backgroundColor: '#273442', minHeight: '100%' }}>
        <div className="sticky">
          <MessagesFilters
            ref={this.filtersRef}
            applyFilters={this.applyFilters}
            toggleAdd={this.toggleAdd}
            counter={counter}
            allowEdit={this.restricted.scope.edit}
          />
        </div>

        {this.state.addNew ? (
          <Message
            key={0}
            item={undefined}
            isNew={true}
            onUpdated={this.messageUpdated}
            onCancel={this.toggleAdd}
            allowEdit={this.restricted.scope.edit}
            API_URL_MESSAGES={this.props.API_URL_MESSAGES}
          />
        ) : null}

        {messages === null ? (
          <Box sx={{ p: 2 }}>
            <Skeleton animation="wave" variant="rounded" width="100%" height={160} />
            <Skeleton animation="wave" variant="rounded" width="100%" height={160} sx={{ mt: 1 }} />
          </Box>
        ) : Array.isArray(messages) && messages.length === 0 && !this.state.error ? (
          <Box sx={{ p: 3, color: '#e3e3e5', opacity: 0.8 }}>
            No messages match your filters. Try clearing or adjusting filters.
          </Box>
        ) : (
          list
        )}

        {this.state.error ? (
          <Box
            role="alert"
            sx={{
              m: 2,
              p: 2,
              borderRadius: 1,
              bgcolor: 'rgba(211, 47, 47, 0.14)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography variant="body2">{this.state.error}</Typography>
            <Button variant="outlined" color="inherit" onClick={this.retry}>
              Retry
            </Button>
          </Box>
        ) : null}

        {messages && this.state.hasMore ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Button
              variant="outlined"
              onClick={this.loadMore}
              disabled={this.state.loadingMore}
              sx={{ color: '#e3e3e5', borderColor: 'rgba(227,227,229,0.5)' }}
              startIcon={this.state.loadingMore ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {this.state.loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          </Box>
        ) : null}
      </div>
    );
  }
}
